import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Wrench,
  ArrowLeft,
  Clock,
  CheckCircle,
  Warning,
  MapPin,
  Calendar,
  PaperPlaneRight,
  Camera,
  File,
  X,
  Check
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

interface OrderIssue {
  id?: string;
  title?: string;
  description?: string;
  urgency_level?: number;
  category?: string;
}

interface OrderProperty {
  id?: string;
  name?: string;
  address?: string;
}

interface OrderData {
  id: string;
  status?: string;
  instructions?: string;
  due_date?: string;
  created_at?: string;
  notes?: string;
  estimated_completion?: string;
  attachments?: string[];
  completion_notes?: string;
  completed_at?: string;
  issue?: OrderIssue;
  property?: OrderProperty;
}

interface Message {
  id: string;
  direction?: string;
  message?: string;
  created_at?: string;
}

export default function SubcontractorOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [order, setOrder] = useState<OrderData | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    notes: '',
    estimated_completion: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('subcontractor_token');
    if (!token) {
      navigate('/subcontractor/login');
      return;
    }
    fetchOrder();
    fetchMessages();
  }, [orderId, navigate]);

  const fetchOrder = async () => {
    const token = localStorage.getItem('subcontractor_token');
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 401) {
        localStorage.removeItem('subcontractor_token');
        navigate('/subcontractor/login');
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setOrder(data);
        setStatusUpdate(prev => ({ ...prev, status: data.status }));
      } else {
        toast.error('Kunde inte hämta order');
        navigate('/subcontractor/orders');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    const token = localStorage.getItem('subcontractor_token');
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/orders/${orderId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) return;
    
    setUpdating(true);
    const token = localStorage.getItem('subcontractor_token');
    
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusUpdate)
      });

      if (res.ok) {
        const data = await res.json();
        setOrder(data);
        toast.success('Status uppdaterad');
      } else {
        toast.error('Kunde inte uppdatera status');
      }
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    
    setSending(true);
    const token = localStorage.getItem('subcontractor_token');
    
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/orders/${orderId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: messageText })
      });

      if (res.ok) {
        setMessageText('');
        fetchMessages();
        toast.success('Meddelande skickat');
      } else {
        toast.error('Kunde inte skicka meddelande');
      }
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status: string | undefined) => {
    const badges: Record<string, { label: string; bg: string; text: string }> = {
      pending: { label: 'Väntande', bg: 'bg-amber-100', text: 'text-amber-700' },
      accepted: { label: 'Accepterad', bg: 'bg-blue-100', text: 'text-blue-700' },
      in_progress: { label: 'Pågår', bg: 'bg-purple-100', text: 'text-purple-700' },
      completed: { label: 'Slutförd', bg: 'bg-green-100', text: 'text-green-700' },
      declined: { label: 'Avböjd', bg: 'bg-red-100', text: 'text-red-700' }
    };
    return badges[status || 'pending'] || badges.pending;
  };

  const getUrgencyColor = (level: number | undefined) => {
    const colors: Record<number, string> = { 1: 'bg-green-500', 2: 'bg-lime-500', 3: 'bg-yellow-500', 4: 'bg-orange-500', 5: 'bg-red-500' };
    return colors[level || 3] || 'bg-slate-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
      </div>
    );
  }

  if (!order) return null;

  const status = getStatusBadge(order.status);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subcontractor-order-detail">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <button
            onClick={() => navigate('/subcontractor/orders')}
            className="p-2 -ml-2 text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-slate-900 truncate">
              {order.issue?.title || 'Arbetsorder'}
            </h1>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
            {status.label}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Order Info */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="text-sm font-medium text-slate-900 mb-4">Orderdetaljer</h2>
          
          <div className="space-y-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Instruktioner</p>
              <p className="text-sm text-slate-900 whitespace-pre-wrap">{order.instructions}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              {order.property && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Plats</p>
                  <p className="flex items-center gap-1 text-slate-900">
                    <MapPin size={14} className="text-slate-400" />
                    {order.property.name}
                  </p>
                  {order.property.address && (
                    <p className="text-xs text-slate-500 ml-[18px]">{order.property.address}</p>
                  )}
                </div>
              )}
              
              {order.due_date && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Deadline</p>
                  <p className="flex items-center gap-1 text-slate-900">
                    <Calendar size={14} className="text-slate-400" />
                    {new Date(order.due_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Issue Details */}
        {order.issue && (
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-sm font-medium text-slate-900 mb-4">Ärende</h2>
            
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-2 h-2 rounded-full ${getUrgencyColor(order.issue?.urgency_level)}`} />
              <span className="text-xs text-slate-500">
                Prioritet {order.issue?.urgency_level || 3}/5
              </span>
              {(order.issue?.urgency_level ?? 0) >= 4 && (
                <span className="text-xs text-red-600 font-medium">Akut</span>
              )}
            </div>
            
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{order.issue.description}</p>
          </div>
        )}

        {/* Status Update */}
        {order.status !== 'completed' && order.status !== 'declined' && (
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-sm font-medium text-slate-900 mb-4">Uppdatera status</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={statusUpdate.status}
                  onValueChange={(value) => setStatusUpdate(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Väntande</SelectItem>
                    <SelectItem value="accepted">Accepterad</SelectItem>
                    <SelectItem value="in_progress">Pågår</SelectItem>
                    <SelectItem value="completed">Slutförd</SelectItem>
                    <SelectItem value="declined">Avböj</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {statusUpdate.status === 'accepted' && (
                <div className="space-y-2">
                  <Label>Beräknat färdigdatum</Label>
                  <Input
                    type="date"
                    value={statusUpdate.estimated_completion}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, estimated_completion: e.target.value }))}
                    data-testid="estimated-completion-input"
                  />
                </div>
              )}
              
              {(statusUpdate.status === 'completed' || statusUpdate.status === 'declined') && (
                <div className="space-y-2">
                  <Label>{statusUpdate.status === 'completed' ? 'Beskrivning av utfört arbete' : 'Anledning'}</Label>
                  <Textarea
                    value={statusUpdate.notes}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={statusUpdate.status === 'completed' ? 'Beskriv vad som gjordes...' : 'Ange anledning...'}
                    rows={4}
                    data-testid="notes-input"
                  />
                </div>
              )}
              
              <Button 
                onClick={handleStatusUpdate} 
                disabled={updating || statusUpdate.status === order.status}
                className="w-full"
                data-testid="update-status-btn"
              >
                {updating ? 'Uppdaterar...' : 'Uppdatera status'}
              </Button>
            </div>
          </div>
        )}

        {/* Completion Info */}
        {order.status === 'completed' && order.completion_notes && (
          <div className="bg-green-50 rounded-xl border border-green-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={20} className="text-green-600" />
              <h2 className="text-sm font-medium text-green-800">Slutförd</h2>
            </div>
            <p className="text-sm text-green-700 whitespace-pre-wrap">{order.completion_notes}</p>
            {order.completed_at && (
              <p className="text-xs text-green-600 mt-2">
                {new Date(order.completed_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="text-sm font-medium text-slate-900">Meddelanden</h2>
          </div>
          
          <div className="max-h-80 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <p className="text-center text-sm text-slate-500 py-8">
                Inga meddelanden ännu
              </p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.direction === 'incoming' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.direction === 'incoming'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.direction === 'incoming' ? 'text-slate-400' : 'text-slate-500'}`}>
                      {msg.created_at ? new Date(msg.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Skriv ett meddelande..."
                rows={2}
                className="flex-1 resize-none"
                data-testid="message-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!messageText.trim() || sending}
                className="self-end"
                data-testid="send-message-btn"
              >
                <PaperPlaneRight size={18} />
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
